// SPDX-License-Identifier: BUSL-1.1

/**
 * Organization context for multi-tenancy support.
 *
 * Provides current organization state and switching capability to all components.
 *
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, shouldRetryApiQuery } from '@/lib/api-client';
import { LoadingScreen } from '@/components/loading-screen';
import { showApiError, showError, showSuccess } from '@/lib/toast';
import { fetchAndStoreBearerJwt, setActiveOrganization } from '@/lib/auth';
import {
  ORGS_QUERY_KEY,
  ORG_SCOPED_BOOTSTRAP_QUERY_KEYS,
  queryKeyLabel,
} from '@/lib/bootstrap-query-keys';
import { Sentry } from '@/lib/sentry';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'paused' | 'deleted';
  membershipRole?: 'owner' | 'admin' | 'member' | 'viewer' | null;
}

interface OrgContextValue {
  organizations: Organization[];
  currentOrg: Organization | null;
  isLoading: boolean;
  error: string | null;
  switchOrg: (orgId: string) => void;
  bootstrapReady: boolean;
  needsOnboarding: boolean;
}

const noop = () => {};

const OrgContext = createContext<OrgContextValue>({
  organizations: [],
  currentOrg: null,
  isLoading: true,
  error: null,
  switchOrg: noop,
  bootstrapReady: false,
  needsOnboarding: false,
});

export function useOrg() {
  return useContext(OrgContext);
}

/** Alias for useOrg() — preferred hook name for components that need current org. */
export { useOrg as useCurrentOrg };

const STORAGE_KEY = 'ceo-org-id';

interface ApiOrgsResponse {
  data: Organization[];
}

/**
 * Organization context provider.
 *
 * Loads user's organizations via the shared QueryClient and provides switching capability.
 * Remembers the last selected org in localStorage.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organizationsQuery = useQuery({
    queryKey: ORGS_QUERY_KEY,
    queryFn: async (): Promise<Organization[]> => {
      Sentry.addBreadcrumb({
        category: 'post-login-bootstrap',
        message: 'orgs-query-started',
        level: 'info',
        data: { queryKey: queryKeyLabel(ORGS_QUERY_KEY) },
      });
      const response = await apiFetch<ApiOrgsResponse>('/orgs', {
        _queryKey: queryKeyLabel(ORGS_QUERY_KEY),
      });
      const orgs = response.data ?? [];
      Sentry.addBreadcrumb({
        category: 'post-login-bootstrap',
        message: 'orgs-query-succeeded',
        level: 'info',
        data: { queryKey: queryKeyLabel(ORGS_QUERY_KEY), count: orgs.length },
      });
      return orgs;
    },
    retry: shouldRetryApiQuery,
    staleTime: 30_000,
  });

  const organizations = organizationsQuery.data ?? [];

  useEffect(() => {
    if (!organizationsQuery.error) {
      setError(null);
      return;
    }

    const err = organizationsQuery.error;

    Sentry.addBreadcrumb({
      category: 'post-login-bootstrap',
      message: 'orgs-query-failed',
      level: 'warning',
      data: {
        queryKey: queryKeyLabel(ORGS_QUERY_KEY),
        errorName: err instanceof Error ? err.name : typeof err,
      },
    });
    const message = err instanceof Error ? err.message : 'Failed to load organizations';
    setError(message);
    showError('Failed to load organizations', message);
  }, [organizationsQuery.error, queryClient]);

  useEffect(() => {
    if (!organizations.length) {
      setCurrentOrgId(null);
      return;
    }

    const storedOrgId = localStorage.getItem(STORAGE_KEY);
    setCurrentOrgId((prev) => {
      if (prev && organizations.some((o) => o.id === prev)) return prev;
      if (storedOrgId && organizations.some((o) => o.id === storedOrgId)) return storedOrgId;
      return organizations[0]?.id ?? null;
    });
  }, [organizations]);

  const resetBootstrapQueriesForOrgSwitch = useCallback(async () => {
    await Promise.all(
      ORG_SCOPED_BOOTSTRAP_QUERY_KEYS.map(async (queryKey) => {
        await queryClient.cancelQueries({ queryKey: [...queryKey], exact: true });
        queryClient.removeQueries({ queryKey: [...queryKey], exact: true });
      }),
    );
  }, [queryClient]);

  const switchOrg = useCallback(
    (orgId: string) => {
      const org = organizations.find((o) => o.id === orgId);

      // Capture previous state so we can roll back if the server rejects the
      // switch — never leave the UI claiming an org the server doesn't know.
      const previousOrgId = currentOrgId;
      const previousStored = localStorage.getItem(STORAGE_KEY);

      // Optimistic UI update — same behaviour as before so single-org users
      // and the pre-lane-3a-auth rollout see no regression.
      setIsSwitchingOrg(true);
      setCurrentOrgId(orgId);
      localStorage.setItem(STORAGE_KEY, orgId);

      // Tell Better Auth which org is now active and re-mint the JWT so the
      // next apiFetch carries the correct `activeOrganizationId` claim. The
      // outer caller fires-and-forgets (the OrgContext API stays sync); we
      // surface failure via toast + rollback inside this IIFE.
      void (async () => {
        try {
          const result = await setActiveOrganization(orgId);

          if (result.ok) {
            // Force a fresh JWT so the new claim is in the Authorization header
            // on the next request. Reusing a cached JWT here would preserve the
            // old activeOrganizationId and cause bootstrap drift after switching.
            const refreshedToken = await fetchAndStoreBearerJwt({ forceRefresh: true });
            if (!refreshedToken) {
              throw new Error('Failed to refresh token after organization switch');
            }
            await resetBootstrapQueriesForOrgSwitch();
            if (org) showSuccess(`Switched to ${org.name}`);
            return;
          }

          if (result.reason === 'unavailable') {
            // Graceful degradation: build-ceo-auth (lane 3a-auth) hasn't shipped the
            // BA organization plugin yet. Fall through to localStorage-only
            // behaviour — single-org users see no change.
            await resetBootstrapQueriesForOrgSwitch();
            if (org) showSuccess(`Switched to ${org.name}`);
            return;
          }

          // Hard failure: roll back so the UI doesn't claim an org the server
          // doesn't know about, and surface the error via the existing toast.
          setCurrentOrgId(previousOrgId);
          if (previousStored) {
            localStorage.setItem(STORAGE_KEY, previousStored);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
          const message = result.message || `HTTP ${result.status ?? 'error'}`;
          showApiError(new Error(`Failed to switch organization: ${message}`));
        } catch (err) {
          setCurrentOrgId(previousOrgId);
          if (previousStored) {
            localStorage.setItem(STORAGE_KEY, previousStored);
          } else {
            localStorage.removeItem(STORAGE_KEY);
          }
          showApiError(
            err instanceof Error ? err : new Error('Failed to switch organization: token refresh failed'),
          );
        } finally {
          setIsSwitchingOrg(false);
        }
      })();
    },
    [organizations, currentOrgId, resetBootstrapQueriesForOrgSwitch],
  );

  const currentOrg = useMemo(
    () => (currentOrgId ? organizations.find((o) => o.id === currentOrgId) ?? null : null),
    [currentOrgId, organizations],
  );

  const isLoading = organizationsQuery.isLoading || isSwitchingOrg || (organizations.length > 0 && !currentOrg);
  const needsOnboarding = !isLoading && organizations.length === 0;
  const bootstrapReady = !isLoading && !!currentOrg;

  const value: OrgContextValue = {
    organizations,
    currentOrg,
    isLoading,
    error,
    switchOrg,
    bootstrapReady,
    needsOnboarding,
  };

  if (isLoading) return <LoadingScreen />;

  return createElement(OrgContext.Provider, { value }, children);
}
